import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getLocation, push, LOCATION_CHANGE } from 'connected-react-router';
import isEmpty from 'lodash/isEmpty';
import { matchPath } from 'connected-react-router';
// (`stopSubmit` from redux-form was used here for per-field error
// display on save failures. After Epic 13f the form is RHF-driven;
// per-field errors land via the snackbar only.)
import { constants as assignedVesselsConstants } from '../assigned-vessels';
import { addSuccess, addError } from '../notifications/actions';
import { deleteItem as deleteDialog } from '../dialog-manager/actions';
import { constants as crewChangeDetailsConstants } from '../crew-change-details/redux';
import * as crewListConstants from '../crew-list/constants';
import { constants as commentsConstants } from '../comments/redux';
import { actions, constants, selector } from './redux';
import Api from '../../utils/Api';
import { getQueryParamsByState, getStateDiffByQuery, getBooleanFilterValues } from './utils';
import ParamsStorage from './ParamsStorage';

// CREW-1256 — `e.apiErrors[0]` assumed every failure was a JSON:API body. A
// network drop, a 500 or a timeout carries no `apiErrors`, so reading `[0]`
// threw INSIDE the catch block — and the user got the same silence the catch
// existed to prevent. Pick the first 422 if there is one, otherwise nothing.
const first422 = (e) => (Array.isArray(e && e.apiErrors)
  ? e.apiErrors.find((apiError) => apiError && apiError.status === '422')
  : undefined);

// CREW-1290 — the board and the slot form share `timeplans.error`, and the form
// showed whatever was there as its own: a drag refused for slot 16625 appeared
// under the Sign off of slot 16555. A refusal says which slot it was about and
// whether the form sent it (only the form sends `status`), so the form can keep
// to its own (`Form/selector.js#getSubmitError`).
const withSource = (e, payload) => (e && typeof e === 'object'
  ? Object.assign(e, { timeplanId: payload.id, fromForm: Boolean(payload.status) })
  : e);

function* fetchItems() {
  try {
    const featureState = yield select(selector);
    const data = yield call(Api.timeplans.index, featureState);

    yield put(actions.addItems(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

function* fetchItem({ payload: { options } }) {
  try {
    const timeplans = yield select(s => s.timeplans.data);
    const ids = timeplans.map(r => r.id);
    const data = yield call(Api.timeplans.show, { ...options, filter: { ...options.filter, id: [options.filter.id, ...ids].join(',') } });
    yield put(actions.addItems(data));
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

function* createItem({ payload }) {
  try {
    const timeplan = yield call(Api.timeplans.create, payload);

    yield put(push('/'));
    yield put(actions.addCreatedItem(timeplan));
    yield put(addSuccess('The timeplan created successfully'));
  } catch (e) {
    console.error(e);
    yield put(actions.createItemError(withSource(e, payload)));

    // CREW-1256 — creating a slot had no error branch at all: `addError`
    // appeared exactly once in this file, in `updateItem`. A refused create
    // stored the error and showed nothing, and unlike update there is not even
    // a drag/resize path to excuse it.
    const apiError = first422(e);
    if (!payload.status && apiError) {
      yield put(addError(apiError.title, e.message));
    }
  }
}

export function* updateItem({ payload }) {
  try {
    const { pathname } = yield select(getLocation);

    const didUpdateByForm = matchPath(pathname, { path: '/timeplans/:id/edit' });
    const didUpdateByVacancy = matchPath(pathname, { path: '/vacancies/:id/edit' });
    const timeplan = yield call(Api.timeplans.update, payload.id, payload);

    if (didUpdateByForm) {
      yield put(push('/'));
    } else if (didUpdateByVacancy) {
      yield put(push('/vacancies'));
    } else {
      const timeplans = yield select((s) => s.timeplans.data);
      const currentTimeplan = timeplans.find((t) => t.id === payload.id);

      if (currentTimeplan.crew_change_sign_on_id) yield put(actions.loadItemsRequest());
    }

    yield put(actions.updateItem(timeplan));
    yield put(addSuccess('The timeplan updated successfully'));
  } catch (e) {
    console.error(e);
    yield put(actions.updateItemError(withSource(e, payload)));

    /**
     * if payload has "status" property the timeplan was updated via
     * the form; otherwise by drag/resize. Drag/resize errors should
     * surface as a snackbar (form won't display them).
     *
     * CREW-1256 — the form half of that promise is kept by `useSubmitErrors`,
     * which now says anything it cannot place on a rendered field. This branch
     * stays responsible only for the board, where there is no form at all.
     */
    const apiError = first422(e);
    if (!payload.status && apiError) {
      yield put(addError(apiError.title, e.message));
    }
  }
}

function* deleteItem({ payload: id }) {
  try {
    yield call(Api.timeplans.delete, id);

    yield put(actions.deleteItem(id));
    yield put(deleteDialog());
    yield put(addSuccess('Planning deleted successfully'));

    const { pathname } = yield select(getLocation);
    const didDeleteByVacancy = matchPath(pathname, { path: '/vacancies/:id/delete' });
    if (didDeleteByVacancy) yield put(push('/vacancies'));
  } catch (e) {
    yield put(actions.createItemError(e));
  }
}

const isWatchedStateField = (fieldName) => [
  'isUserSectionOpen',
  'cellWidth',
  'startDate',
  'viewport',
].includes(fieldName);

function* updateUrlAndBrowserStorage({ type, payload }) {
  try {
    if (type === constants.CHANGE_STATE && !isWatchedStateField(payload.field)) return;

    const featureState = yield select((s) => s.timeplans);
    const query = getQueryParamsByState(featureState, 'timeplans');

    ParamsStorage.setQuery('timeplans', query);

    yield put(push(query));

    if (payload && payload.field === 'viewport') {
      yield put(actions.loadItemsRequest());
    }
  } catch (e) {
    console.error(e);
  }
}

export function* syncUrlParams(action) {
  try {
    const featureState = yield select((s) => s.timeplans);
    const location = yield select(getLocation);
    const hasFilterInState = !isEmpty(featureState.appliedFilter);
    const queryFromStorage = ParamsStorage.getQuery('timeplans');
    const hasParamsInUrl = Boolean(location.search);
    const hasParamsInStorage = Boolean(queryFromStorage);

    let query;
    let diff;

    if (hasFilterInState) {
      const urlQueryByState = getQueryParamsByState(featureState, 'timeplans');
      const areUrlParamsActualWithFilter = location.search === urlQueryByState;

      if (!areUrlParamsActualWithFilter) {
        query = urlQueryByState;
      }
    } else if (hasParamsInUrl) {
      diff = getStateDiffByQuery(location.search, 'timeplans');
    } else if (hasParamsInStorage) {
      diff = ParamsStorage.getQueryAsStateDiff('timeplans');
      query = queryFromStorage;
    } else {
      diff = {
        filter: {
          ...getBooleanFilterValues(action.payload.roleIds, 'roles'),
          ...getBooleanFilterValues(action.payload.vesselIds, 'vessels'),
        },
      };

      query = getQueryParamsByState({ ...featureState, ...diff, appliedFilter: diff.filter }, 'timeplans');
    }

    if (query) yield put(push(query));
    if (diff) yield put(actions.updateStateByParams(diff));

    yield put(actions.changeState('isReadyToRenderScheduler', true));
  } catch (e) {
    console.error(e);
  }
}

function* redirectIfReloadNewForm({ payload: { isFirstRendering, location } }) {
  const isNewFormWithoutNeededData = (
    matchPath(location.pathname, { path: '/timeplans/new' })
    || matchPath(location.pathname, '/timeplans/crew-rotation/crew-change/new')
  ) && isFirstRendering;

  if (isNewFormWithoutNeededData) {
    yield put(push('/'));
  }
}



/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    takeEvery(constants.CREATE_ITEM_REQUEST, createItem),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery(constants.DELETE_ITEM_REQUEST, deleteItem),
    takeEvery(constants.LOAD_ITEM_REQUEST, fetchItem),
    // takeLatest (CREW-1268): a newer request cancels the one in flight, so an
    // older answer arriving last cannot overwrite the list.
    takeLatest([
      constants.LOAD_ITEMS_REQUEST,
      crewChangeDetailsConstants.ADD_CREATED_ITEM,
      crewChangeDetailsConstants.ITEM_UPDATED_SUCCESSFULLY,
      crewChangeDetailsConstants.ITEM_DELETED_SUCCESSFULLY,

      crewListConstants.ADD_CREATED_ITEM,
      crewListConstants.ITEM_UPDATED_SUCCESSFULLY,
      crewListConstants.ITEM_DELETED_SUCCESSFULLY,

      commentsConstants.DELETE_ITEM_REQUEST,
    ], fetchItems),
    takeEvery(assignedVesselsConstants.ITEMS_LOADED_SUCCESSFULLY, syncUrlParams),
    takeEvery([
      constants.CHANGE_STATE,
      constants.APPLY_FILTER,
    ], updateUrlAndBrowserStorage),
    takeEvery(LOCATION_CHANGE, redirectIfReloadNewForm),
  ];
}
